import { Translate, useTranslate } from '@bynder/localization';
import { useHasAllPermissions } from '../../../../helpers/hooks';
import { IMAGE_UPLOADER_REQUIRED_PERMISSIONS } from '../../../../constants/widgets';
import { CVAsset, CVFile, ModalType } from '../../../../types';
import { AssetType } from '../../../../types/assetTypes';
import { Box, Button, Card, Flex } from '@bynder/design-system';
import { IconImageSearch, IconDelete } from '@bynder/icons';
import { ImageWidgetUploader } from './ImageWidgetUploader';
import { useModalStore } from '../../../../stores/modalStore';
import { UploadType } from '../../../../hooks/useUploader';
import CropperKebabMenu from '../../../pages/CropperKebabMenu';
import {
  CroppedImage,
  Aspect,
  useCropper,
  resetImageCrop,
  isImageCropped,
} from '@bynder/react-image-cropper-lib';

import {
  ActiveSplits,
  useReleaseToggle,
} from '../../../../helpers/releaseToggle';

const ImageInspectorPanelSelector = ({
  uploadType,
  handleAssetSelect,
  selectedAssetId,
  handleOnClearImage,
  handleOnSetImage,
  handleUploadCompleted,
  imageUrl,
  assetName,
  imageWidth,
  imageHeight,
}: {
  uploadType: UploadType;
  handleAssetSelect: (selectedMedia: CVAsset[], file: CVFile) => void;
  handleUploadCompleted: (mediaId: string) => void;
  selectedAssetId?: string | null;
  handleOnClearImage: () => void;
  handleOnSetImage: (imageUrl: string) => void;
  imageUrl: string | null;
  assetName?: string;
  imageWidth?: number;
  imageHeight?: number;
}) => {
  const { translate } = useTranslate();
  const canUseUploader = useHasAllPermissions(
    IMAGE_UPLOADER_REQUIRED_PERMISSIONS,
  );
  const openModal = useModalStore.use.openModal();
  const modalProps = {
    compactViewProps: {
      selectedAssets: [selectedAssetId],
      assetTypes: [AssetType.IMAGE, AssetType.DOCUMENT],
    },
  };
  const handleModalOpen = () => {
    openModal({
      modalType: ModalType.COMPACT_VIEW_MODAL,
      modalOnSubmit: handleAssetSelect,
      modalProps,
    });
  };

  const openCropper = useCropper();

  const oldContextAction = (
    <Card.ContextActionButton
      tooltip={translate('WIDGET_INSPECTOR.CLEAR_IMAGE_BUTTON')}
      aria-label="Clear image"
      onClick={handleOnClearImage}
    >
      <IconDelete />
    </Card.ContextActionButton>
  );

  const newContextAction = (
    <CropperKebabMenu
      actions={[
        {
          for: 'CROP',
          onClick: () =>
            openCropper({
              aspect:
                uploadType === UploadType.Card ? Aspect.COVER : Aspect.CUSTOM,
              src: imageUrl,
              onSuccess: croppedImage => {
                handleOnSetImage(croppedImage);
              },
            }),
        },
        isImageCropped(imageUrl) && {
          for: 'RESET_CROP',
          onClick: () => {
            handleOnSetImage(resetImageCrop(imageUrl));
          },
        },
        {
          for: 'CLEAR_IMAGE',
          onClick: handleOnClearImage,
        },
      ]}
    />
  );

  const { data: isCropperSplitActive } = useReleaseToggle(
    ActiveSplits.imageCropperRollout,
  );

  const contextAction = isCropperSplitActive
    ? newContextAction
    : oldContextAction;

  return imageUrl ? (
    <Box paddingBottom="2" paddingTop="4">
      <Card
        variant="clean"
        onClick={handleModalOpen}
        contextAction={contextAction}
      >
        <Card.Frame>
          {isCropperSplitActive ? (
            <CroppedImage
              contentResize="fit"
              src={imageUrl}
              absolutePositioning
            />
          ) : (
            <Card.Images>
              <Card.Img contentResize="fit" src={imageUrl} />
            </Card.Images>
          )}
        </Card.Frame>

        <Card.Header
          title={assetName ?? ''}
          detail={
            imageWidth && imageHeight ? `${imageWidth} x ${imageHeight}` : ''
          }
        />
      </Card>
    </Box>
  ) : (
    <Box paddingBottom="2" paddingTop="4">
      <Flex gap="3">
        <Button
          isFullWidth
          icon={<IconImageSearch />}
          variant="secondary"
          onClick={handleModalOpen}
        >
          <Translate id="PAGE_INSPECTOR.DROPDOWN_COMPACT_VIEW" />
        </Button>
        <ImageWidgetUploader
          uploadType={uploadType}
          canUseUploader={canUseUploader}
          handleUploadCompleted={handleUploadCompleted}
        />
      </Flex>
    </Box>
  );
};

export default ImageInspectorPanelSelector;
